import React, { useEffect, useState } from "react";
import { Attention, Button, Checkbox, Steps, Table } from "@optimizely/axiom";
import { SidebarFooter } from "../../Sidebar/SidebarFooter";
import { Invitation } from "../../../../domain/Invitation";
import { User } from "../../../../domain/User";
import { useForm } from "react-hook-form";
import { isValidEmail } from "../../../../lib/utils";
import { EmailSelectorInput } from "../EmailSelectorInput/EmailSelectorInput";
import { InvitationAccessForm } from "../InvitationAccess/InvitationAccessForm";
import { useUserContext } from "../../../../providers/UserProvider";
import { AccessDetailsTable } from "../AccessDetailsTable/AccessDetailsTable";
import { AccessDetailsByProductId } from "../../../../domain/AccessDetails";
import { emitToast } from "../../../../lib/toaster-utils";
import {
    INVITATION_ERROR_MESSAGE,
    INVITATION_SUCCESS_MESSAGE,
    REFETCH_PERMISSIONS_EVENT_NAME
} from "../../../../constants/constants";
import { adaptApiErrors, IApiError } from "../../../../services/ErrorMessageAdapter";
import { useAnalyticsTracking } from "../../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getGrantPermissionsPayload } from "../../../../lib/analytics-helpers";
import { ANALYTICS_FLOWS, ANALYTICS_TRACKED_COMPONENTS } from "../../../../constants/analytics-constants";
import { useLocation } from "react-router-dom";
import { useOrganization } from "../../../../hooks/useOrganization/useOrganization";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";
import { toSelectedUserGroups, UserGroupWithProjectDetails } from "../../../../lib/access-helpers";
import { GroupsWithoutProductAccessList } from "../GroupsWithoutProductAccess/GroupsWithoutProductAccessList";

const STEPS = [
    { label: "User List", value: 1 },
    { label: "Product Access", value: 2 },
    { label: "Review", value: 3 }
];
interface IInvitationFormValues {
    firstName: string;
    lastName: string;
    apiError: { message: string };
}
export const InvitationFormWithSteps = ({
    onCreate,
    usersFlow = false
}: {
    onCreate: ({
        newInvitation,
        inviteAnother
    }: {
        newInvitation: Invitation | null;
        inviteAnother: boolean;
    }) => Promise<boolean>;
    usersFlow?: boolean;
}) => {
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User>();
    const [email, setEmail] = useState<string | null>(null);
    const [inviteAnother, setInviteAnother] = useState(false);
    const [requireAcceptance, setRequireAcceptance] = useState<boolean>(true);
    const { organizationId, profile } = useUserContext();
    const [accessDetailsByProductId, setAccessDetailsByProductId] = useState<AccessDetailsByProductId>({});
    const [groupsWithoutProductAccess, setGroupsWithoutProductAccess] = useState<UserGroupWithProjectDetails[]>([]);
    const [inviteAnotherAccess, setInviteAnotherAccess] = useState<AccessDetailsByProductId | undefined>(undefined);
    const [inviteAnotherGroupsWithoutAccess, setInviteAnotherGroupsWithoutAccess] = useState<
        UserGroupWithProjectDetails[] | undefined
    >(undefined);
    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        setError,
        reset,
        watch,
        formState: { errors }
    } = useForm<IInvitationFormValues>({ mode: "onChange" });

    watch("firstName");
    watch("lastName");

    const { sendTrackEvent } = useAnalyticsTracking();
    const { pathname } = useLocation();
    const isProductAccessFlow = pathname.includes("/access/");
    const { organization, loading: organizationLoading } = useOrganization({ id: organizationId });
    const { enabled: hideForceAcceptance } = useFeatureFlag(Flags.HIDE_FORCE_ACCEPTANCE_CHECKBOX);
    const { enabled: enableVirtualAgentUsers } = useFeatureFlag(Flags.ENABLE_VIRTUAL_AGENT_USERS);
    // Opal supplies virtual users with a placeholder last name, so the invite flow
    // collapses their name down to a single read-only "Name" field/column. The stored
    // last name is left untouched and still submitted with the invitation.
    const isVirtualSelectedUser = enableVirtualAgentUsers && !!selectedUser?.isVirtual;
    const showReqAcceptanceChkBox =
        !organizationLoading && !organization?.ssoEnabled && usersFlow && !hideForceAcceptance;

    const emailError = email === null || isValidEmail(email) ? "" : "Please enter a valid email";

    const createNewInvitation = () => {
        const { firstName, lastName } = getValues();
        const selectedRoleGroups = toSelectedUserGroups({ accessDetailsByProductId, groupsWithoutProductAccess });

        if (selectedRoleGroups?.length && email) {
            return new Invitation({
                firstName,
                lastName,
                email: email,
                requireAcceptance: showReqAcceptanceChkBox ? requireAcceptance : false,
                organizationId: organizationId!,
                userGroups: selectedRoleGroups?.map((g) => ({ id: g.id!, name: g.name! })),
                createdBy: profile?.email!,
                created: new Date(),
                modified: new Date()
            });
        }

        return null;
    };

    const handleError = (apiErrors: IApiError[] | Error) => {
        if (Array.isArray(apiErrors)) {
            const errors = adaptApiErrors(apiErrors);
            errors.forEach((error) => {
                setError(error.name as any, {
                    type: "individualFieldApiError",
                    message: error.message
                });
            });
        } else {
            setError("apiError", {
                type: "apiError",
                message: INVITATION_ERROR_MESSAGE
            });
        }
    };

    const handleFormSubmission = async () => {
        const newInvitation = createNewInvitation();
        const selectedRoleGroups = toSelectedUserGroups({ accessDetailsByProductId, groupsWithoutProductAccess });
        setSaving(true);

        try {
            const success = await onCreate({ newInvitation, inviteAnother });

            if (success) {
                const [userGroupIds, userGroupNames, projectIds, projectNames] = selectedRoleGroups?.reduce(
                    (acc: [string[], string[], string[], string[]], group: Partial<UserGroupWithProjectDetails>) => {
                        acc[0].push(group.id!);
                        acc[1].push(group.name!);
                        if (group.projectName) {
                            acc[2].push(group.projectId!);
                            acc[3].push(group.projectName!);
                        } else if (group.projectIds?.length && group.projectNames?.length) {
                            acc[2].push(...group.projectIds);
                            acc[3].push(...group.projectNames);
                        }

                        return acc;
                    },
                    [[], [], [], []]
                );
                const payload = getGrantPermissionsPayload({
                    component: ANALYTICS_TRACKED_COMPONENTS.INVITATION_SIDEBAR,
                    userGroupIds,
                    userGroupNames,
                    email: newInvitation?.email!,
                    projectIds,
                    projectNames,
                    flow: isProductAccessFlow ? ANALYTICS_FLOWS.PRODUCT_ACCESS : ANALYTICS_FLOWS.USER_PAGE
                });
                sendTrackEvent(payload);

                // reset form values if invite another is checked
                if (inviteAnother) {
                    setInviteAnotherAccess(structuredClone(accessDetailsByProductId));
                    setInviteAnotherGroupsWithoutAccess(structuredClone(groupsWithoutProductAccess));
                    setSelectedUser(undefined);
                    setEmail(null);
                    setStep(1);
                    reset();
                }
                emitToast({ message: INVITATION_SUCCESS_MESSAGE });
                window.dispatchEvent(new CustomEvent(REFETCH_PERMISSIONS_EVENT_NAME));
            }
        } catch (error) {
            handleError(error as IApiError[] | Error);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (email !== selectedUser?.email) {
            // Always clear name fields when email changes
            setValue("firstName", "", { shouldValidate: false });
            setValue("lastName", "", { shouldValidate: false });

            // Only clear selectedUser and access when transitioning away from an existing user
            // (not when editing email for new users where selectedUser is undefined)
            if (selectedUser) {
                setSelectedUser(undefined);
                setAccessDetailsByProductId({});
            }
        }
    }, [email, selectedUser, setValue]);
    const nextStep = () => {
        setStep((curr) => curr + 1);
    };

    const previousStep = () => {
        setStep((curr) => curr - 1);
    };

    const disableContinue = () => {
        if (step === 1) {
            // Virtual users only surface a single "Name" field, so the hidden last name
            // is not part of the completeness check.
            const nameIncomplete = isVirtualSelectedUser
                ? !getValues("firstName")
                : !getValues("firstName") || !getValues("lastName");

            return !!emailError || !email || nameIncomplete;
        }
        if (step === 2) {
            // A selected group that grants no product access is a real selection even though it
            // adds no product rows, so gate on both slices (EPTS-6429).
            return Object.keys(accessDetailsByProductId).length === 0 && groupsWithoutProductAccess.length === 0;
        }
        return false;
    };

    return (
        <form className="flex flex--column" onSubmit={handleSubmit(handleFormSubmission)} data-cy="invitation-form">
            {errors.apiError && (
                <Attention alignment="left" className="push--top push-quad--bottom push-quad--sides" type="bad-news">
                    {errors.apiError.message}
                </Attention>
            )}
            <div className="flex flex-column">
                <Steps items={STEPS} currentStep={step} userCanEdit={false} className="push-quad--top"></Steps>
                <div style={{ display: step === 1 ? "block" : "none" }}>
                    <EmailSelectorInput
                        email={email}
                        emailError={emailError}
                        onSelectUser={async (user) => {
                            if (user?.email) {
                                setSelectedUser(user);

                                setValue("firstName", user.firstName, { shouldValidate: true });
                                setValue("lastName", user.lastName, { shouldValidate: true });
                                setEmail(user.email);
                            } else if (selectedUser) {
                                // Clear selectedUser if undefined is passed (e.g., disabled list item clicked)
                                // to prevent inconsistent state where name inputs are disabled but blank
                                setSelectedUser(undefined);
                            }
                        }}
                        onChange={async (email) => {
                            setEmail(email);
                            if (!email) {
                                setValue("firstName", "");
                                setValue("lastName", "");
                            }
                        }}
                        className="push-quad--ends soft-quad--sides"
                    />
                    <div className="flex flex--row soft-quad--sides">
                        {isVirtualSelectedUser ? (
                            <div
                                className={`push-double--bottom width--1-1 ${errors.firstName && "oui-form-bad-news"}`}
                                data-cy="invitation-virtual-user-name"
                            >
                                <label className="oui-label" htmlFor="invitation-name">
                                    Name
                                    <span aria-label="(required)" className="oui-label--required" />
                                </label>

                                <input
                                    aria-describedby="invitation-name-error"
                                    className="oui-text-input"
                                    id="invitation-name"
                                    type="text"
                                    disabled
                                    {...register("firstName", { required: true })}
                                />
                                {/*
                                    Keep the stored last name in form state so it is still submitted
                                    with the invitation, without displaying or exposing it for editing.
                                */}
                                <input type="hidden" {...register("lastName")} />
                                {errors.firstName && (
                                    <span className="oui-form-note" id="invitation-name-error">
                                        Name is required
                                    </span>
                                )}
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`push-double--bottom width--1-2 ${
                                        errors.firstName && "oui-form-bad-news"
                                    }`}
                                >
                                    <label className="oui-label" htmlFor="invitation-first-name">
                                        First Name
                                        <span aria-label="(required)" className="oui-label--required" />
                                    </label>

                                    <input
                                        aria-describedby="invitation-first-name-error"
                                        className="oui-text-input"
                                        id="invitation-first-name"
                                        type="text"
                                        disabled={!!selectedUser || !email || !!emailError}
                                        {...register("firstName", { required: true })}
                                    />
                                    {errors.firstName && (
                                        <span className="oui-form-note" id="invitation-first-name-error">
                                            First name is required
                                        </span>
                                    )}
                                </div>

                                <div
                                    className={`push-quad--bottom soft-double--left width--1-2 ${
                                        errors.lastName && "oui-form-bad-news"
                                    }`}
                                >
                                    <label className="oui-label" htmlFor="invitation-last-name">
                                        Last Name
                                        <span aria-label="(required)" className="oui-label--required" />
                                    </label>

                                    <input
                                        aria-describedby="invitation-last-name-error"
                                        className="oui-text-input"
                                        id="invitation-last-name"
                                        type="text"
                                        disabled={!!selectedUser || !email || !!emailError}
                                        {...register("lastName", { required: true })}
                                    />
                                    {errors.lastName && (
                                        <span className="oui-form-note" id="invitation-last-name-error">
                                            Last name is required
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    {showReqAcceptanceChkBox && (
                        <div className="push-quad--bottom soft-quad--sides">
                            <Checkbox
                                checked={requireAcceptance}
                                label="Require Acceptance"
                                data-cy="require-acceptance-checkbox"
                                description="The user must accept the invitation sent to their inbox"
                                onChange={() => setRequireAcceptance(!requireAcceptance)}
                            />
                        </div>
                    )}
                </div>
                <div style={{ display: step === 2 ? "block" : "none" }}>
                    <InvitationAccessForm
                        key={`${email}-${selectedUser?.id || "new"}`}
                        className="push-quad--bottom soft-quad--sides"
                        email={isValidEmail(email || "") ? email : ""}
                        organizationId={organizationId}
                        selectedUser={selectedUser}
                        onAccessDetailsChange={setAccessDetailsByProductId}
                        onGroupsWithoutProductAccessChange={setGroupsWithoutProductAccess}
                        initialAccessDetails={inviteAnotherAccess}
                        initialGroupsWithoutProductAccess={inviteAnotherGroupsWithoutAccess}
                    />
                </div>
                <div style={{ display: step === 3 ? "block" : "none" }} data-cy="invitation-review-step">
                    <div className="soft-quad--sides push-quad--top">
                        <div className="push-quad--bottom" data-cy="review-user-details">
                            <h5 className="push--bottom">You're about to invite:</h5>
                            <div className="soft-double--left">
                                <Table density="tight">
                                    <Table.THead>
                                        <Table.TR>
                                            <Table.TH sorting={{}}>Email</Table.TH>
                                            {isVirtualSelectedUser ? (
                                                <Table.TH sorting={{}}>Name</Table.TH>
                                            ) : (
                                                <>
                                                    <Table.TH sorting={{}}>First Name</Table.TH>
                                                    <Table.TH sorting={{}}>Last Name</Table.TH>
                                                </>
                                            )}
                                        </Table.TR>
                                    </Table.THead>
                                    <Table.TBody>
                                        <Table.TR data-cy="review-user-row">
                                            <Table.TD data-cy="review-user-email">{email}</Table.TD>
                                            {isVirtualSelectedUser ? (
                                                <Table.TD data-cy="review-user-name">{getValues("firstName")}</Table.TD>
                                            ) : (
                                                <>
                                                    <Table.TD data-cy="review-user-first-name">
                                                        {getValues("firstName")}
                                                    </Table.TD>
                                                    <Table.TD data-cy="review-user-last-name">
                                                        {getValues("lastName")}
                                                    </Table.TD>
                                                </>
                                            )}
                                        </Table.TR>
                                    </Table.TBody>
                                </Table>
                            </div>
                        </div>

                        <div className="push-quad--bottom" data-cy="review-product-access">
                            <h5 className="push--bottom">with the following Product access(es):</h5>
                            {Object.keys(accessDetailsByProductId).length === 0 &&
                            groupsWithoutProductAccess.length === 0 ? (
                                <div className="soft-double--left">
                                    <em>No access selected</em>
                                </div>
                            ) : (
                                <>
                                    {Object.keys(accessDetailsByProductId).length > 0 && (
                                        <div data-cy="review-access-table">
                                            <AccessDetailsTable accessDetails={accessDetailsByProductId} />
                                        </div>
                                    )}
                                    {/*
                                        Step 2 stays mounted under `display: none`, so its copy of
                                        this list is still in the DOM. Distinct selector keeps
                                        queries unambiguous.
                                    */}
                                    <GroupsWithoutProductAccessList
                                        groups={groupsWithoutProductAccess}
                                        testId="review-groups-without-product-access"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex">
                <SidebarFooter
                    leftSide={
                        step === 3
                            ? [
                                  <Checkbox
                                      key="invite-another"
                                      label="Invite another"
                                      checked={inviteAnother}
                                      onChange={(event) => {
                                          setInviteAnother(event.target.checked);
                                      }}
                                      data-cy="invite-another-checkbox"
                                  />
                              ]
                            : undefined
                    }
                >
                    <>
                        {step > 1 && (
                            <Button onClick={previousStep} style="plain">
                                Back
                            </Button>
                        )}
                        {step === 3 ? (
                            <Button
                                key="send-invitation-button"
                                className=""
                                isLoading={saving}
                                loadingText="Saving"
                                style="highlight"
                                isSubmit
                            >
                                Send
                            </Button>
                        ) : (
                            <Button
                                data-cy="invitation-form-continue"
                                onClick={nextStep}
                                isDisabled={disableContinue()}
                                style="highlight"
                            >
                                Continue
                            </Button>
                        )}
                    </>
                </SidebarFooter>
            </div>
        </form>
    );
};

InvitationFormWithSteps.displayName = "InvitationFormWithSteps";
